import { Controller } from "@hotwired/stimulus"
import { loadPdfjs } from "../lib/pdfjs_loader"

export default class extends Controller {
  static targets = [
    "canvas",
    "container",
    "pageInput",
    "pageCurrent",
    "pageTotal",
    "zoomLevel",
    "loading",
    "error"
  ]

  static values = {
    url: String,
    page: { type: Number, default: 1 },
    scale: { type: Number, default: 1 },
    minScale: { type: Number, default: 0.25 },
    maxScale: { type: Number, default: 4 }
  }

  connect() {
    this.pdfDoc = null
    this.pageRendering = false
    this.pageNumPending = null
    this.setupKeyboardShortcuts()
    this.loadPdf()
  }

  disconnect() {
    this.removeKeyboardShortcuts()
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
    }
  }

  setupKeyboardShortcuts() {
    this.boundKeyHandler = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundKeyHandler)
  }

  removeKeyboardShortcuts() {
    document.removeEventListener("keydown", this.boundKeyHandler)
  }

  handleKeydown(event) {
    // Ignore the shortcut while the user is typing in any editable field, so
    // arrow keys move the caret (and +/-/= type literally) instead of paging or
    // zooming the PDF. Covers inputs, textareas, selects, and contenteditable
    // regions like the File Hub rich text block editor (a <div>, not an <input>).
    if (this.isEditableTarget(event.target)) return

    switch (event.key) {
      case "ArrowLeft":
        event.preventDefault()
        this.prevPage()
        break
      case "ArrowRight":
        event.preventDefault()
        this.nextPage()
        break
      case "+":
      case "=":
        event.preventDefault()
        this.zoomIn()
        break
      case "-":
        event.preventDefault()
        this.zoomOut()
        break
    }
  }

  // True when the key event originated in an editable field, so the global PDF
  // shortcut should stand aside. `isContentEditable` covers the rich text editor
  // and any nested element inside a contenteditable region.
  isEditableTarget(target) {
    if (!target) return false
    if (target.isContentEditable) return true

    return ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)
  }

  async loadPdf() {
    if (!this.urlValue) return

    this.showLoading()

    try {
      // Fetch PDF as ArrayBuffer to avoid 304 response issues
      const response = await fetch(this.urlValue, {
        cache: "no-store"
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      const pdfData = await response.arrayBuffer()
      const pdfjsLib = await loadPdfjs()
      const loadingTask = pdfjsLib.getDocument({ data: pdfData })
      this.pdfDoc = await loadingTask.promise
      
      if (this.hasPageTotalTarget) {
        this.pageTotalTarget.textContent = this.pdfDoc.numPages
      }
      
      // Calculate initial scale to fit width
      await this.calculateFitWidth()
      
      this.hideLoading()
      this.renderPage(this.pageValue)
    } catch (error) {
      console.error("Error loading PDF:", error)
      this.showError("Failed to load PDF document")
    }
  }

  async calculateFitWidth() {
    if (!this.pdfDoc) return

    const page = await this.pdfDoc.getPage(1)
    const viewport = page.getViewport({ scale: 1 })
    this.scaleValue = this.containerContentWidth() / viewport.width
    this.updateZoomDisplay()
  }

  // The container's inner width/height (clientWidth/Height minus its own
  // padding), so "fit" fills the area exactly regardless of how it's padded —
  // the File Hub area has no padding, the purchases card has 24px.
  containerContentWidth() {
    const styles = getComputedStyle(this.containerTarget)
    const paddingX = parseFloat(styles.paddingLeft) + parseFloat(styles.paddingRight)
    return this.containerTarget.clientWidth - paddingX
  }

  containerContentHeight() {
    const styles = getComputedStyle(this.containerTarget)
    const paddingY = parseFloat(styles.paddingTop) + parseFloat(styles.paddingBottom)
    return this.containerTarget.clientHeight - paddingY
  }

  async renderPage(pageNum) {
    if (!this.pdfDoc) return

    if (this.pageRendering) {
      this.pageNumPending = pageNum
      return
    }

    this.pageRendering = true
    this.pageValue = pageNum

    try {
      const page = await this.pdfDoc.getPage(pageNum)
      const viewport = page.getViewport({ scale: this.scaleValue })

      const canvas = this.canvasTarget
      const context = canvas.getContext("2d")

      // Set canvas dimensions for high DPI displays
      const outputScale = window.devicePixelRatio || 1
      canvas.width = Math.floor(viewport.width * outputScale)
      canvas.height = Math.floor(viewport.height * outputScale)
      canvas.style.width = Math.floor(viewport.width) + "px"
      canvas.style.height = Math.floor(viewport.height) + "px"

      const transform = outputScale !== 1
        ? [outputScale, 0, 0, outputScale, 0, 0]
        : null

      const renderContext = {
        canvasContext: context,
        transform: transform,
        viewport: viewport
      }

      await page.render(renderContext).promise

      this.pageRendering = false
      this.updatePageInput()

      if (this.pageNumPending !== null) {
        this.renderPage(this.pageNumPending)
        this.pageNumPending = null
      }
    } catch (error) {
      console.error("Error rendering page:", error)
      this.pageRendering = false
    }
  }

  // Page Navigation
  prevPage() {
    if (this.pageValue <= 1) return
    this.renderPage(this.pageValue - 1)
  }

  nextPage() {
    if (!this.pdfDoc || this.pageValue >= this.pdfDoc.numPages) return
    this.renderPage(this.pageValue + 1)
  }

  goToPage(event) {
    const pageNum = parseInt(event.target.value, 10)
    if (isNaN(pageNum) || !this.pdfDoc) return

    const validPage = Math.max(1, Math.min(pageNum, this.pdfDoc.numPages))
    this.renderPage(validPage)
  }

  // Jump to a page carried as a Stimulus action param, used by the File Hub's
  // left-panel "Web version" blocks (click->pdf-player#goToPageNumber). A no-op
  // until the PDF has loaded, and yields while the document is being edited so a
  // block click edits the block instead of navigating.
  goToPageNumber(event) {
    if (!this.pdfDoc) return
    if (this.element.classList.contains("is-editing")) return

    const page = event.params.page
    const validPage = Math.max(1, Math.min(page, this.pdfDoc.numPages))
    this.renderPage(validPage)
  }

  updatePageInput() {
    if (this.hasPageInputTarget) {
      this.pageInputTarget.value = this.pageValue
    }
    // The File Hub toolbar shows the page as text ("Page X of N") instead of an input.
    if (this.hasPageCurrentTarget) {
      this.pageCurrentTarget.textContent = this.pageValue
    }
  }

  // Zoom Controls
  zoomIn() {
    const newScale = Math.min(this.scaleValue * 1.25, this.maxScaleValue)
    this.setScale(newScale)
  }

  zoomOut() {
    const newScale = Math.max(this.scaleValue / 1.25, this.minScaleValue)
    this.setScale(newScale)
  }

  fitWidth() {
    this.calculateFitWidth().then(() => {
      this.renderPage(this.pageValue)
    })
  }

  fitPage() {
    if (!this.pdfDoc) return

    this.pdfDoc.getPage(this.pageValue).then((page) => {
      const viewport = page.getViewport({ scale: 1 })
      const scaleX = this.containerContentWidth() / viewport.width
      const scaleY = this.containerContentHeight() / viewport.height

      this.setScale(Math.min(scaleX, scaleY))
    })
  }

  setScale(scale) {
    this.scaleValue = scale
    this.updateZoomDisplay()
    this.renderPage(this.pageValue)
  }

  updateZoomDisplay() {
    if (this.hasZoomLevelTarget) {
      this.zoomLevelTarget.textContent = Math.round(this.scaleValue * 100) + "%"
    }
  }

  // Loading/Error States
  showLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.add("visible")
    }
    if (this.hasErrorTarget) {
      this.errorTarget.classList.remove("visible")
    }
  }

  hideLoading() {
    if (this.hasLoadingTarget) {
      this.loadingTarget.classList.remove("visible")
    }
  }

  showError(message) {
    this.hideLoading()
    if (this.hasErrorTarget) {
      this.errorTarget.textContent = message
      this.errorTarget.classList.add("visible")
    }
  }
}
